/**
 * Payments and coupons: schemas and pure mappers (P3, P4).
 * Endpoints: GET /api/payments/config, POST /api/payments/finalize,
 * PATCH /api/reservations/:id/resume-payment (codes of PAY-11), GET /api/coupons/check.
 */
import { z } from 'zod';
import { messageMap, ns } from '@/i18n';
import type { CouponCheck, PaymentConfig, PaymentFinalizeResult } from '../../domain';

const s = z.string().nullish();
const n = z.number().nullish();
const b = z.boolean().nullish();

export const PaymentConfigSchema = z.object({ configured: b, publishableKey: s, currency: s }).passthrough();

export function toPaymentConfig(c: z.infer<typeof PaymentConfigSchema>): PaymentConfig {
  const key = c.publishableKey?.trim() || null;
  return { provider: 'stripe', publishableKey: key, configured: c.configured === true && !!key };
}

export const FinalizeSchema = z
  .object({ success: b, paymentStatus: s, authorized: b, reservationId: s, reservationStatus: s })
  .passthrough();

/** PAY-04: a payment is complete when Stripe holds (`requires_capture`) or took (`succeeded`) the money. */
export const isPaidStatus = (status: string | null | undefined) =>
  status === 'requires_capture' || status === 'succeeded';

export function toFinalizeResult(r: z.infer<typeof FinalizeSchema>): PaymentFinalizeResult {
  const status = r.paymentStatus ?? 'unknown';
  return { status, paid: isPaidStatus(status), reservationId: r.reservationId ?? undefined };
}

export const CouponCheckSchema = z
  .object({
    success: b,
    data: z.object({ code: z.string(), label: s, clientDiscountPct: n }).passthrough(),
  })
  .passthrough();

export function toCouponCheck(r: z.infer<typeof CouponCheckSchema>): CouponCheck {
  return {
    code: r.data.code.trim().toUpperCase(),
    label: r.data.label?.trim() ?? '',
    pct: Math.max(0, Number(r.data.clientDiscountPct) || 0),
  };
}

/** Messages of the coupon check (the server answers in English), current language. */
export const COUPON_MESSAGES = messageMap(ns.errors, {
  empty: 'coupon.empty',
  notFound: 'coupon.notFound',
  limit: 'coupon.limit',
});

/** Codes of `PATCH /api/reservations/:id/resume-payment` (PaymentResumeError, RATE_UNAVAILABLE) → our text. */
export const RESUME_MESSAGES: Readonly<Record<string, string>> = messageMap(ns.errors, {
  ALREADY_PAID: 'resume.ALREADY_PAID',
  VEHICLE_UNAVAILABLE: 'resume.VEHICLE_UNAVAILABLE',
  EXPIRED_CART: 'resume.EXPIRED_CART',
  EXPIRED_DATES: 'resume.EXPIRED_DATES',
  NOT_RESUMABLE: 'resume.NOT_RESUMABLE',
  NOT_ONLINE: 'resume.NOT_ONLINE',
  RATE_UNAVAILABLE: 'resume.RATE_UNAVAILABLE',
});
